<template>
  <div class="v2-style">
    <div class="timeline-table__card">
      <div class="timeline-table__card-header">
        <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
          <span class="badge btn-success">{{ selectedIdsLength }}</span>
          {{ $t('global.object.selectedRow') }}
          <a class="color-primary" @click="handleUnselectAll">{{ $t('global.action.deselect') }}</a>
        </h6>
      </div>
      <div
        ref="timelineContainer"
        class="timeline-table__container"
        :style="timelineContainerStyle"
      >
        <!-- Sticky Header in First Row and First Col -->
        <div class="timeline-table__sticky-header-row">
          <div class="timeline-table__sticky-header-col">
            <div
              v-if="!allVehiclesHaveFleetTask"
              class="timeline-table__sticky-header-col-checkbox"
            >
              <McCheckbox
                :modelValue="selectedAll"
                :disabled="disableSelectAll || isPending || isCreatingFO"
                @change="handleSelectAll"
              />
            </div>

            <div class="timeline-table__sticky-header-col-container">
              <div class="timeline-table__sticky-header-col-info">
                <div class="__license-plate">
                  {{ `${$t('global.fleet')} (${vehicles.length})` }}
                </div>
                <p class="mb-0 mt-1 pt-1">
                  {{ $t('global.average') }}:
                  <i class="icon-outline_calculate me-1" />
                  {{ formatOrDash(showResponse.average_distance, 2, 1000) }} km
                </p>
                <div class="__average-metrics">
                  <span>
                    <i class="icon-fill_weight me-1" />
                    {{ $t('global.field.weight') }}:
                    <span
                      :class="
                        getPercentageColorClass(showResponse.average_weight_percentage?.color)
                      "
                      >({{ formatOrDash(showResponse.average_weight_percentage?.value) }}%)</span
                    >
                  </span>
                  <span class="mx-2">&bull;</span>
                  <span>
                    <i class="icon-fill_cube me-1" />
                    {{ $t('global.field.volume') }}:
                    <span
                      :class="
                        getPercentageColorClass(showResponse.average_volume_percentage?.color)
                      "
                      >({{ formatOrDash(showResponse.average_volume_percentage?.value) }}%)</span
                    >
                  </span>
                </div>
              </div>

              <b-button
                v-if="!isPending"
                variant="outline"
                class="timeline-table__sticky-header-col-icon-eye btn"
                @click="handleToggleShowAll"
              >
                <i :class="iconToggleShowAll" />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Scroll Header and Body -->
        <div
          ref="gridScrollContainer"
          :class="`timeline-table__grid${isPending ? '' : '-scroll'}`"
          @dragover="handleGridDragOver"
        >
          <!-- Header -->
          <div :class="`timeline-table__grid${isPending ? '' : '-scroll'}-header`">
            <div class="timeline-table__sticky-header-col"></div>
            <div class="timeline-table__header-cols-scroll-x">
              <!-- Header contain time info -->
              <div
                v-for="(time, index) in timeCols"
                :key="`timecols-${index}`"
                class="timeline-table__header-cols"
              >
                <div class="timeline-table__header-col">
                  {{ time }}
                </div>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="timeline-table__grid-scroll-body">
            <!-- First col that sticky -->
            <div class="timeline-table__first-sticky-cols">
              <template v-if="isPending">
                <div
                  v-for="index in 3"
                  :key="`first-sticky-col-${index}`"
                  class="timeline-table__first-sticky-col"
                >
                  <div class="timeline-table__first-sticky-col-checkbox">
                    <McCheckbox :disabled="true" />
                  </div>

                  <div class="timeline-table__first-sticky-col-container">
                    <b-skeleton class="flex-grow-1" />
                  </div>
                </div>
              </template>

              <template v-else>
                <div
                  v-for="(vehicle, index) in vehicles"
                  :key="`first-sticky-col-${vehicle.id}`"
                  class="timeline-table__first-sticky-col"
                >
                  <div
                    v-if="!allVehiclesHaveFleetTask"
                    class="timeline-table__first-sticky-col-checkbox"
                  >
                    <McCheckbox
                      :modelValue="selected[vehicle.id]"
                      :disabled="vehicle.fleet_task_id || isPending || isCreatingFO"
                      @change="handleSelectVehicle($event, vehicle)"
                    />
                  </div>

                  <div class="timeline-table__first-sticky-col-container">
                    <div class="timeline-table__vehicle-info">
                      <div
                        class="timeline-table__vehicle-info-license-plate d-flex justify-content-between align-items-center"
                      >
                        <span class="d-inline-flex align-items-center gap-1">
                          {{
                            hasAccessAll(['route-optimization-manually-assign-vehicle:read'])
                              ? !vehicle?.is_vehicle_assigned
                                ? `TRIP-${getResequenceTrip(index)}`
                                : vehicle.license_plate
                              : vehicle?.license_plate
                          }}
                          <McBadge
                            v-if="vehicle?.hull_no && vehicle?.is_vehicle_assigned"
                            :text="vehicle.hull_no"
                            variant="outlinePlainSecondary"
                            size="medium"
                            type="button"
                          />
                        </span>
                        <span
                          v-if="canAssignVehicleManually && !vehicle?.fleet_task_id"
                          class="d-inline-flex align-items-center gap-1 ms-auto"
                        >
                          <McButton
                            :variant="vehicle?.is_vehicle_assigned ? 'icon-only' : 'primary'"
                            :text="!vehicle?.is_vehicle_assigned ? $t('global.action.choose') : ''"
                            :icon="
                              vehicle?.is_vehicle_assigned
                                ? 'icon-outline_edit-2'
                                : 'icon-outline_car'
                            "
                            size="small"
                            iconPosition="right"
                            class="mt-n1"
                            :borderNone="vehicle?.is_vehicle_assigned"
                            @click="handleAssignVehicle(vehicle)"
                          />
                        </span>
                      </div>
                      <div class="timeline-table__vehicle-info-details mt-1 pt-1">
                        <div class="d-flex align-items-center mb-2">
                          <span
                            class="timeline-table__vehicle-info-order-count cursor-pointer"
                            @click="handleShowDetailVehicleDelivery(vehicle)"
                          >
                            {{ vehicle.order_count }} {{ $t('global.delivery') }}
                          </span>
                          <div>
                            <span class="mx-2">&bull;</span>
                            <span>
                              <i class="icon-outline_calculate me-1" />
                              {{ formatOrDash(vehicle.distance, 2, 1000) }} km
                            </span>
                          </div>
                        </div>

                        <div class="d-flex justify-content-between">
                          <div>
                            <div>
                              <i class="icon-fill_weight me-1" />
                              {{ $t('global.field.weight') }}
                              <span
                                :class="getPercentageColorClass(vehicle.weight_percentage?.color)"
                                >({{ formatOrDash(vehicle.weight_percentage?.value) }}%)</span
                              >
                            </div>
                            <div>
                              {{ formatOrDash(vehicle.total_weight) }}/{{
                                formatOrDash(vehicle.weight_capacity)
                              }}
                              kg
                            </div>
                          </div>
                          <div>
                            <div>
                              <i class="icon-fill_cube me-1" />
                              {{ $t('global.field.volume') }}
                              <span
                                :class="getPercentageColorClass(vehicle.volume_percentage?.color)"
                                >({{ formatOrDash(vehicle.volume_percentage?.value) }}%)</span
                              >
                            </div>
                            <div>
                              {{ formatOrDash(vehicle.total_volume) }}/{{
                                formatOrDash(vehicle.volume_capacity)
                              }}
                              cbm
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="vehicle.is_error"
                      class="timeline-table__sticky-header-col-icon-warning"
                    >
                      <i :id="`popover-target-${vehicle.id}`" class="icon-fill_alert-triangle" />
                      <b-popover
                        no-fade
                        triggers="hover"
                        custom-class="timeline-table__popover-alert"
                        :target="`popover-target-${vehicle.id}`"
                      >
                        <template #default>
                          <div v-if="vehicleErrors[vehicle.id]">
                            {{ $t('routeOptimization.alert.vehicleRemoved') }}
                          </div>
                          <div v-if="isSolutionError({ vehicle, type: 'delivery-order' })">
                            {{ $t('routeOptimization.alert.invalidDeliveryStatus') }}
                          </div>
                          <div
                            v-if="
                              isDeliveryOrderUserGroupInvalid({ vehicle, type: 'delivery-order' })
                            "
                          >
                            {{ $t('routeOptimization.alert.invalidDeliveryUserGroup') }}
                          </div>
                        </template>
                      </b-popover>
                    </div>
                    <b-button
                      variant="outline"
                      class="timeline-table__sticky-header-col-icon-eye btn"
                      @click="handleToggleShowRoute(vehicle)"
                    >
                      <i
                        :class="showedRoutes[vehicle.id] ? 'icon-fill_eye' : 'icon-fill_eye-off'"
                      />
                    </b-button>
                  </div>
                </div>
              </template>
            </div>

            <!-- Other cols that has no sticky -->
            <div class="timeline-table__grid-scroll-rows">
              <!-- Cols if it's in PENDING status -->
              <template v-if="isPending">
                <div class="timeline-table__grid-scroll-col">
                  <div class="timeline-table__grid-scroll-col-grow">
                    <div class="loader-wrapper">
                      <b-spinner class="align-middle mb-2" />
                      <strong>{{ $t('global.text.loading') }}...</strong>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <template v-if="vehicles.length > 0">
                  <div
                    v-for="vehicle in vehicles"
                    :key="`grid-scroll-cols-${vehicle.id}`"
                    class="timeline-table__grid-scroll-cols"
                  >
                    <!-- Event Rows -->
                    <div
                      class="timeline-table__timeline-row droppable-row"
                      @dragover.prevent
                      @drop="onDropToTimeline($event, vehicle)"
                    >
                      <TimelineItem
                        :vehicle="vehicle"
                        :totalMinutes="totalMinutes"
                        :expectedStartTime="showResponse.expected_departure_on"
                      />

                      <div>
                        <template v-for="point in getFilteredTimelinesByVehicle(vehicle.id)">
                          <div
                            :key="`timeline-event-item-${point.point_id}`"
                            :class="[
                              {
                                'draggable-point': canDragDropPoint(vehicle.id, point.point_id),
                              },
                            ]"
                            :draggable="canDragDropPoint(vehicle.id, point.point_id)"
                            @dragstart="
                              canDragDropPoint(vehicle.id, point.point_id) &&
                                onDragStart(
                                  point,
                                  vehicle,
                                  getPointIndexByVehicle(vehicle.id, point.point_id)
                                )
                            "
                            @dragend="onDragEnd"
                            @dragover.prevent
                            @drop="
                              ;(canDragDropPoint(vehicle.id, point.point_id) ||
                                isStartPickupDefaultPointByVehicle(vehicle.id, point.point_id)) &&
                                onDrop(
                                  point,
                                  vehicle,
                                  getPointIndexByVehicle(vehicle.id, point.point_id),
                                  $event
                                )
                            "
                          >
                            <EventItem
                              :id="`timeline-event-item-${point.point_id}`"
                              ref="eventItem"
                              :key="`timeline-event-item-${point.point_id}`"
                              :index="getPointIndexByVehicle(vehicle.id, point.point_id)"
                              :point="point"
                              :color="vehicle.color"
                              :totalMinutes="totalMinutes"
                              :expectedStartTime="showResponse.expected_departure_on"
                              :isStartPoint="
                                isStartPickupDefaultPointByVehicle(vehicle.id, point.point_id)
                              "
                            />
                            <PopoverItem :key="`popover-item-${point.point_id}`" :point="point" />
                          </div>
                        </template>
                      </div>
                    </div>

                    <!-- Cols contain time info -->
                    <div
                      v-for="timeCol in timeCols"
                      :key="`grid-scroll-col-${timeCol}`"
                      class="timeline-table__grid-scroll-col"
                    >
                      <div class="timeline-table__grid-scroll-col-grow">
                        <div></div>
                        <div></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="timeline-table__grid-scroll-cols empty-table-container">
                    <img src="@/assets/images/warning-icon.png" />
                    <div class="content-wrapper">
                      <strong>Tidak ada pengiriman yang berhasil dioptimasi</strong>
                      <p>Coba ubah pengaturan dan lakukan optimasi ulang</p>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <DetailVehicleDeliveryModal
      :vehicle="selectedVehicleForDetail"
      :settings="settings"
      :containsFleetTask="disableSelectAll"
      @hasBeenAddedToTrip="(roId, data, res) => $emit('hasBeenAddedToTrip', roId, data, res)"
    />
    <ModalValueHelp
      :context="vehicleModalContext"
      :attrs="vehicleModalAttrs"
      :multiple="false"
      :fullObject="selectedVehicleObject"
      @update:fullObject="handleVehicleSelected"
    >
      <template #detail>
        <VehicleDetail :vehicle="currentVehicle" />
      </template>
      <template #districts="{ value }">
        <McChipList
          v-if="value?.length"
          :items="value"
          :max-visible="3"
          itemTextField="name"
          showAllMode="dropdown"
          :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
          showAllDropdownTrigger="action"
        />
        <span v-else>-</span>
      </template>
      <template #license_plate="{ item, value }">
        <div>{{ value || item?.license_plate || '-' }}</div>
        <div v-if="item?.hull_no" class="text-muted">
          {{ item.hull_no }}
        </div>
      </template>
      <template #recommendation_statuses="{ item }">
        <div v-if="item.recommendation_statuses && item.recommendation_statuses.length">
          <span
            v-for="(status, index) in item.recommendation_statuses"
            :key="index"
            :class="{ 'text-danger': !['RECOMMENDED', 'FLEET_TASK_CREATED'].includes(status) }"
          >
            <br v-if="index > 0" />
            {{ $t(`routeOptimization.error.${toCamelCase(status)}`) }}
          </span>
        </div>
        <span v-else>-</span>
      </template>
    </ModalValueHelp>
    <ConfirmToChangeVehicle
      :vehicle="selectedVehicleObject"
      ref="confirmToChangeVehicle"
      @confirmChangeVehicle="handleConfirmVehicleChange"
    />
  </div>
</template>

<script>
import McCheckbox from '@/components/McCheckbox'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import McChipList from '@/components/McChipList'
import { mapGetters, mapState } from 'vuex'
import { Helper } from '@/mixin/helper'

import EventItem from '../../../components/EventItem'
import PopoverItem from '../../../components/PopoverItem'
import TimelineItem from '../../../components/TimelineItem'
import DetailVehicleDeliveryModal from '../components/DetailVehicleDeliveryModal'
import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'
import VehicleDetail from '../components/VehicleDetail.vue'
import ConfirmToChangeVehicle from '../components/ConfirmToChangeVehicle.vue'

import { formatOrDash } from '@/helpers/format-number.js'
import { getSettingVehicle } from '@/services/rest-api/lib/route-optimization'

const INCLUDED_TIMELINE_TYPES = ['BREAK', 'IDLE', 'END']
const EXCLUDED_POINT_INDEX_TYPES = ['BREAK', 'IDLE', 'END']
const AUTO_SCROLL_EDGE_THRESHOLD = 80
const AUTO_SCROLL_MAX_STEP = 24
const EMPTY_TIMELINE_META = Object.freeze({
  filteredTimelines: [],
  pointIndexes: {},
  draggablePoints: {},
  startPickupDefaultPointId: null,
})

export default {
  name: 'ServedContent',
  components: {
    McCheckbox,
    EventItem,
    TimelineItem,
    PopoverItem,
    McBadge,
    DetailVehicleDeliveryModal,
    McButton,
    ModalValueHelp,
    VehicleDetail,
    McChipList,
    ConfirmToChangeVehicle,
  },
  mixins: [Helper],
  props: {
    isShowMap: {
      type: Boolean,
      default: true,
    },
    isCreatingFO: {
      type: Boolean,
      default: true,
    },
    showResponse: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Object,
      default: () => {},
    },
    selectedAll: {
      type: Boolean,
      default: true,
    },
    selectedIdsLength: {
      type: Number,
      default: 0,
    },
    timeCols: {
      type: Array,
      default: () => [],
    },
    totalMinutes: {
      type: Number,
      default: 0,
    },
    hasFOCreated: {
      type: Boolean,
      default: false,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
    isRoSwatActive: {
      type: Boolean,
      default: false,
    },
    routeOptimizationId: {
      type: [String, Number],
      required: true,
    },
    settings: {
      type: Object,
      default: () => {},
    },
  },
  emits: [
    'update:selected',
    'update:selectedAll',
    'selectAll',
    'toggleShowAll',
    'toggleShowRoute',
    'selectVehicle',
    'dragStart',
    'dragEnd',
    'drop',
    'dropToTimeline',
    'unselectAll',
    'assignVehicle',
    'hasBeenAddedToTrip',
  ],
  data() {
    return {
      autoScrollRafId: null,
      autoScrollStepX: 0,
      autoScrollStepY: 0,
      expandedContainerHeight: 300,
      heightSyncTimer: null,
      postExpandHeightSyncTimer: null,
      selectedVehicleForDetail: null,
      currentVehicle: null,
      selectedVehicleObject: null,
      showVehicleModal: false,
      isChangeVehicle: false,
    }
  },
  computed: {
    ...mapState('routeOptimization', ['showedRoutes']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    vehicles() {
      return this.showResponse.vehicles || []
    },
    iconToggleShowAll() {
      const isAllShowed = Object.keys(this.showedRoutes).every((key) => {
        return this.showedRoutes[key]
      })

      if (isAllShowed) {
        return 'icon-fill_eye'
      }

      const isAllNotShowed = Object.keys(this.showedRoutes).every((key) => {
        return this.showedRoutes[key] === false
      })

      if (isAllNotShowed) {
        return 'icon-fill_eye-off'
      }

      return 'icon-fill_eye'
    },
    disableSelectAll() {
      return this.showResponse?.vehicles?.some((vehicle) => vehicle.fleet_task_id !== null)
    },
    vehicleErrors() {
      const vehicles = {}

      this.showResponse.vehicles.forEach((vehicle) => {
        if (!vehicle.is_error) return
        if (vehicle.license_plate !== 'Data Telah Dihapus') return

        vehicles[vehicle.id] = true
      })

      return vehicles
    },
    allVehiclesHaveFleetTask() {
      return this.showResponse?.vehicles?.every((vehicle) => vehicle.fleet_task_id !== null)
    },
    timelineContainerStyle() {
      if (this.vehicles.length === 1) return {}

      const collapsedHeight = 300
      const targetHeight = this.isShowMap
        ? collapsedHeight
        : Math.max(this.expandedContainerHeight, collapsedHeight)

      return {
        height: `${targetHeight}px`,
        maxHeight: `${targetHeight}px`,
      }
    },
    timelineMetaByVehicle() {
      const timelineMetaByVehicle = {}

      for (const vehicle of this.vehicles) {
        const timelines = vehicle?.timelines || []
        const startPointId = this.getStartPointIdFromTimelines(timelines)
        const startPickupDefaultPointId = this.getStartPickupDefaultPointIdFromTimelines(timelines)
        const filteredTimelines = []
        const pointIndexes = {}
        const draggablePoints = {}

        let pointIndex = 0

        for (const point of timelines) {
          const pointId = point.point_id
          const isHiddenStartPoint = pointId === startPointId
          const isStartPickupDefaultPoint = pointId === startPickupDefaultPointId

          if (!isHiddenStartPoint) {
            const isIncludedTimelineType = INCLUDED_TIMELINE_TYPES.includes(point.type)
            const isVisibleDefaultPoint = point.type === 'DEFAULT' && point.service_time !== 'PT0S'

            if (isIncludedTimelineType || isStartPickupDefaultPoint || isVisibleDefaultPoint) {
              filteredTimelines.push(point)
            }
          }

          if (EXCLUDED_POINT_INDEX_TYPES.includes(point.type) || isStartPickupDefaultPoint) {
            pointIndexes[pointId] = null
          } else {
            if (!isHiddenStartPoint && point.type === 'DEFAULT' && point.service_time !== 'PT0S') {
              pointIndex++
            }

            pointIndexes[pointId] = pointIndex
          }

          draggablePoints[pointId] =
            !this.hasFOCreated &&
            point.type === 'DEFAULT' &&
            !point.is_error &&
            !isStartPickupDefaultPoint
        }

        timelineMetaByVehicle[vehicle.id] = {
          filteredTimelines,
          pointIndexes,
          draggablePoints,
          startPickupDefaultPointId,
        }
      }

      return timelineMetaByVehicle
    },
    vehicleModalListGetter() {
      const rov_id = this.currentVehicle?.id
      return getSettingVehicle(this.routeOptimizationId, rov_id)
    },
    hasFeatureVehicleDistrictMgm() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.vehicle_district
      )
    },
    vehicleModalAttrs() {
      const fields = [
        {
          key: 'license_plate',
          name: 'license_plate',
          label: this.$t('routeOptimization.fields.licensePlate'),
          isSlot: true,
          slotComponent: 'DynamicCell',
          subKey: 'hull_no',
        },
        {
          key: 'districts',
          name: 'districts',
          label: this.$t('routeOptimization.fields.operationalArea'),
          isSlot: true,
          isShow: this.hasFeatureVehicleDistrictMgm,
          tdAttr: {
            style: 'min-width: 400px',
          },
          thAttr: {
            style: 'min-width: 400px',
          },
        },
        {
          key: 'vehicle_type.type',
          label: this.$t('routeOptimization.fields.vehicleType'),
        },
        {
          key: 'box_type.name',
          label: this.$t('routeOptimization.fields.boxType'),
        },
        {
          key: 'driver.name',
          label: this.$t('routeOptimization.fields.driver'),
        },
        {
          key: 'box_type.weight_capacity',
          label: this.$t('routeOptimization.fields.weightCapacity'),
        },
        {
          key: 'box_type.volume_capacity',
          label: this.$t('routeOptimization.fields.volumeCapacity'),
        },
        {
          key: 'recommendation_statuses',
          name: 'recommendation_statuses',
          isSlot: true,
          label: this.$t('routeOptimization.fields.description'),
          tdAttr: {
            style: 'right:0px;position:sticky;z-index:20;border-left:1px solid #e6e7ec;',
          },
          thAttr: {
            style: 'top:0;right:0px;position:sticky;z-index:30;border-left:1px solid #e6e7ec',
          },
        },
      ]

      return {
        size: 'xl',
        name: 'change-vehicle',
        modalTitle: 'Kendaraan',
        trackBy: 'id',
        displayedBy: 'license_plate',
        subKey: 'hull_no',
        listGetter: this.vehicleModalListGetter,
        isMandatory: true,
        disableRowWhen: (item) => {
          return item.recommendation_statuses?.includes('FLEET_TASK_CREATED')
        },
        tableFields: fields.filter((field) => field.isShow !== false),
      }
    },
    vehicleModalContext() {
      return {
        model: this.selectedVehicleObject?.id,
        blurHandler: () => {},
      }
    },
    canAssignVehicleManually() {
      const options = this.settings?.options || {}
      const manuallyAssignVehicle = options?.manually_assign_vehicle || false
      return (
        manuallyAssignVehicle &&
        this.hasAccessAll(['route-optimization-manually-assign-vehicle:read'])
      )
    },
  },
  watch: {
    isShowMap() {
      this.$nextTick(() => {
        this.scheduleExpandedContainerHeightSync()
        if (!this.isShowMap) {
          this.schedulePostExpandHeightSync()
        }
        this.syncRowHeights()
      })
    },
    showResponse: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.syncRowHeights()
        })
      },
    },
  },
  mounted() {
    window.addEventListener('resize', this.refreshExpandedContainerHeight)
    window.addEventListener('resize', this.syncRowHeights)
    this.scheduleExpandedContainerHeightSync()
    this.$nextTick(() => {
      this.syncRowHeights()
    })
  },
  beforeDestroy() {
    this.stopAutoScroll()
    this.stopExpandedContainerHeightSync()
    this.stopPostExpandHeightSync()
    window.removeEventListener('resize', this.refreshExpandedContainerHeight)
    window.removeEventListener('resize', this.syncRowHeights)
  },
  methods: {
    formatOrDash,
    getResequenceTrip(currentIndex) {
      let count = 0
      for (let i = 0; i <= currentIndex; i++) {
        count++
      }
      return count
    },
    syncRowHeights() {
      this.$nextTick(() => {
        const rootElement = this.$el
        if (!rootElement) return

        const stickyCols = rootElement.querySelectorAll('.timeline-table__first-sticky-col')
        const timelineCols = rootElement.querySelectorAll('.timeline-table__grid-scroll-cols')

        stickyCols.forEach((stickyCol, index) => {
          if (timelineCols[index]) {
            const timelineCol = timelineCols[index]

            stickyCol.style.height = 'auto'
            timelineCol.style.height = 'auto'

            const maxHeight = Math.max(stickyCol.offsetHeight, timelineCol.offsetHeight)

            stickyCol.style.height = `${maxHeight}px`
            timelineCol.style.height = `${maxHeight}px`
          }
        })
      })
    },
    scheduleExpandedContainerHeightSync() {
      this.stopExpandedContainerHeightSync()
      this.refreshExpandedContainerHeight()
      this.syncRowHeights()

      let frameCount = 0
      const maxFrameCount = 2
      const syncOnNextFrame = () => {
        this.refreshExpandedContainerHeight()
        this.syncRowHeights()
        frameCount++

        if (frameCount >= maxFrameCount) {
          this.heightSyncTimer = null
          this.stopExpandedContainerHeightSync()
          return
        }

        this.heightSyncTimer = requestAnimationFrame(syncOnNextFrame)
      }

      this.heightSyncTimer = requestAnimationFrame(syncOnNextFrame)
    },
    stopExpandedContainerHeightSync() {
      if (!this.heightSyncTimer) return

      cancelAnimationFrame(this.heightSyncTimer)
      this.heightSyncTimer = null
    },
    schedulePostExpandHeightSync() {
      this.stopPostExpandHeightSync()

      this.postExpandHeightSyncTimer = setTimeout(() => {
        this.refreshExpandedContainerHeight()
        this.syncRowHeights()
        this.postExpandHeightSyncTimer = null
      }, 400)
    },
    stopPostExpandHeightSync() {
      if (!this.postExpandHeightSyncTimer) return

      clearTimeout(this.postExpandHeightSyncTimer)
      this.postExpandHeightSyncTimer = null
    },
    refreshExpandedContainerHeight() {
      const container = this.$refs.timelineContainer
      if (!container) return

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const containerTop = container.getBoundingClientRect().top
      const bottomOffset = 16
      const availableHeight = viewportHeight - containerTop - bottomOffset

      this.expandedContainerHeight = Math.max(availableHeight, 300)
    },
    getGridScrollContainer() {
      return this.$refs.gridScrollContainer || null
    },
    getAutoScrollStep(distanceToEdge) {
      const ratio = (AUTO_SCROLL_EDGE_THRESHOLD - distanceToEdge) / AUTO_SCROLL_EDGE_THRESHOLD
      const normalizedRatio = Math.min(Math.max(ratio, 0), 1)
      return Math.ceil(normalizedRatio * AUTO_SCROLL_MAX_STEP)
    },
    handleGridDragOver(event) {
      const scrollContainer = this.getGridScrollContainer()
      if (!scrollContainer) return

      event.preventDefault()

      const boundingRect = scrollContainer.getBoundingClientRect()
      const topDistance = event.clientY - boundingRect.top
      const bottomDistance = boundingRect.bottom - event.clientY
      const leftDistance = event.clientX - boundingRect.left
      const rightDistance = boundingRect.right - event.clientX

      let verticalStep = 0
      let horizontalStep = 0

      if (topDistance < AUTO_SCROLL_EDGE_THRESHOLD) {
        verticalStep = -this.getAutoScrollStep(topDistance)
      } else if (bottomDistance < AUTO_SCROLL_EDGE_THRESHOLD) {
        verticalStep = this.getAutoScrollStep(bottomDistance)
      }

      if (leftDistance < AUTO_SCROLL_EDGE_THRESHOLD) {
        horizontalStep = -this.getAutoScrollStep(leftDistance)
      } else if (rightDistance < AUTO_SCROLL_EDGE_THRESHOLD) {
        horizontalStep = this.getAutoScrollStep(rightDistance)
      }

      this.autoScrollStepY = verticalStep
      this.autoScrollStepX = horizontalStep

      if (verticalStep === 0 && horizontalStep === 0) {
        this.stopAutoScroll()
        return
      }

      this.startAutoScrollLoop()
    },
    startAutoScrollLoop() {
      if (this.autoScrollRafId) return

      const tick = () => {
        const scrollContainer = this.getGridScrollContainer()
        if (!scrollContainer) {
          this.stopAutoScroll()
          return
        }

        if (this.autoScrollStepY === 0 && this.autoScrollStepX === 0) {
          this.autoScrollRafId = null
          return
        }

        if (this.autoScrollStepY !== 0) {
          const previousTop = scrollContainer.scrollTop
          scrollContainer.scrollTop += this.autoScrollStepY
          if (previousTop === scrollContainer.scrollTop) {
            this.autoScrollStepY = 0
          }
        }

        if (this.autoScrollStepX !== 0) {
          const previousLeft = scrollContainer.scrollLeft
          scrollContainer.scrollLeft += this.autoScrollStepX
          if (previousLeft === scrollContainer.scrollLeft) {
            this.autoScrollStepX = 0
          }
        }

        this.autoScrollRafId = requestAnimationFrame(tick)
      }

      this.autoScrollRafId = requestAnimationFrame(tick)
    },
    stopAutoScroll() {
      if (this.autoScrollRafId) {
        cancelAnimationFrame(this.autoScrollRafId)
        this.autoScrollRafId = null
      }

      this.autoScrollStepX = 0
      this.autoScrollStepY = 0
    },
    handleSelectAll(value) {
      this.$emit('selectAll', value)
    },
    handleToggleShowAll() {
      this.$emit('toggleShowAll')
    },
    handleToggleShowRoute(vehicle) {
      this.$emit('toggleShowRoute', vehicle)
    },
    handleSelectVehicle(value, vehicle) {
      this.$emit('selectVehicle', value, vehicle)
    },
    getVehicleTimelineMeta(vehicleId) {
      return this.timelineMetaByVehicle[vehicleId] || EMPTY_TIMELINE_META
    },
    getFilteredTimelinesByVehicle(vehicleId) {
      return this.getVehicleTimelineMeta(vehicleId).filteredTimelines
    },
    canDragDropPoint(vehicleId, pointId) {
      return this.getVehicleTimelineMeta(vehicleId).draggablePoints[pointId] === true
    },
    isStartPickupDefaultPointByVehicle(vehicleId, pointId) {
      return this.getVehicleTimelineMeta(vehicleId).startPickupDefaultPointId === pointId
    },
    getPointIndexByVehicle(vehicleId, pointId) {
      const pointIndex = this.getVehicleTimelineMeta(vehicleId).pointIndexes[pointId]
      return pointIndex === undefined ? null : pointIndex
    },
    onDragStart(point, vehicle, index) {
      if (point?.type === 'START') return
      if (this.isStartPickupDefaultPointByVehicle(vehicle?.id, point?.point_id)) return
      this.stopAutoScroll()
      this.$emit('dragStart', point, vehicle, index)
    },
    onDragEnd() {
      this.stopAutoScroll()
      this.$emit('dragEnd')
    },
    onDrop(targetPoint, targetVehicle, targetIndex, event) {
      this.stopAutoScroll()
      if (targetPoint?.type === 'START') return
      this.$emit('drop', targetPoint, targetVehicle, targetIndex, event)
    },
    onDropToTimeline(event, vehicle) {
      this.stopAutoScroll()
      this.$emit('dropToTimeline', event, vehicle)
    },
    handleUnselectAll() {
      this.$emit('unselectAll')
    },
    isSolutionError({ vehicle }) {
      // if is_error in vehicle is false, the vehicle has no error at all
      if (!vehicle.is_error) {
        return false
      }

      // set error delivery order
      let result = false
      for (const point of vehicle.timelines) {
        for (const activity of point.activities) {
          if (!activity.delivery_order.is_error) continue

          result =
            activity.delivery_order.errors?.length > 0 &&
            activity.delivery_order.errors[0] ===
              'FLEET_TASK.ROUTE_OPTIMIZATION_ACTIVITY_DELIVERY_ORDER_INVALID_STATUS'
          break
        }

        if (result) break
      }

      return result
    },
    isDeliveryOrderUserGroupInvalid({ vehicle }) {
      // if is_error in vehicle is false, the vehicle has no error at all
      if (!vehicle.is_error) {
        return false
      }

      // set error delivery order
      let result = false
      for (const point of vehicle.timelines) {
        for (const activity of point.activities) {
          if (!activity.delivery_order.is_error) continue

          result =
            activity.delivery_order.errors?.length > 0 &&
            activity.delivery_order.errors[0] ===
              'FLEET_TASK.ROUTE_OPTIMIZATION.DELIVERY_ORDER_USER_GROUP_ID.DIFFERENT'
          break
        }

        if (result) break
      }

      return result
    },
    getStartPointIdFromTimelines(timelines) {
      if (!this.isRoSwatActive) return null

      return timelines.find((point) => point.type === 'START')?.point_id || null
    },
    getStartPickupDefaultPointIdFromTimelines(timelines) {
      if (!this.isRoSwatActive) return null

      const startPoint = timelines.find((point) => point.type === 'START')
      const pickupDefaultPoint = timelines.find((point) => {
        if (point.type !== 'DEFAULT') return false

        const hasPickupActivity = point.activities?.some((activity) => activity.type === 'PICKUP')
        if (!hasPickupActivity) return false

        const startAddressId = startPoint?.address?.id
        const pointAddressId = point.address?.id

        if (startAddressId != null && pointAddressId != null) {
          return startAddressId === pointAddressId
        }

        return startPoint?.address?.name === point.address?.name
      })

      if (!startPoint || !pickupDefaultPoint) return null

      return pickupDefaultPoint.point_id
    },
    getPercentageColorClass(color) {
      if (color === '#C31700') {
        return 'text-danger'
      } else if (color === '#CC6E00') {
        return 'text-warning'
      } else if (color === '#0F6344') {
        return 'text-success'
      } else {
        return ''
      }
    },
    handleShowDetailVehicleDelivery(vehicle) {
      this.selectedVehicleForDetail = vehicle
      this.$nextTick(() => {
        this.$bvModal.show('detail-vehicle-delivery-modal')
      })
    },
    handleAssignVehicle(vehicle) {
      this.currentVehicle = vehicle
      this.selectedVehicleObject = vehicle?.is_vehicle_assigned
        ? { ...vehicle, id: vehicle.vehicle_id }
        : null
      this.$nextTick(() => {
        this.$bvModal.show(`${this.vehicleModalAttrs.name}-modal-value-help`)
      })
    },
    toCamelCase(str) {
      return str.toLowerCase().replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
    },
    handleVehicleSelected(selectedObject) {
      // Safety check: don't allow selecting vehicle with FLEET_TASK_CREATED status
      if (selectedObject?.recommendation_statuses?.includes('FLEET_TASK_CREATED')) {
        this.$mcToast.warning('Kendaraan dengan status FLEET_TASK_CREATED tidak dapat dipilih')
        return
      }

      this.selectedVehicleObject = selectedObject

      if (this.selectedVehicleObject?.assignment_status === 'ASSIGNED_TO_OTHER_TRIP') {
        this.isChangeVehicle = true
        this.$nextTick(() => {
          this.$bvModal.show('confirm-to-change-vehicle')
        })
        return
      }

      this.$emit('assignVehicle', {
        routeOptimizationId: this.routeOptimizationId,
        currentVehicleId: this.currentVehicle.id,
        data: {
          action: 'ASSIGN',
          vehicle_id: selectedObject.id,
        },
      })

      this.$bvModal.hide(`${this.vehicleModalAttrs.name}-modal-value-help`)
    },
    handleVehicleModalHidden() {
      this.currentVehicle = null
      this.selectedVehicleObject = null
    },
    handleConfirmVehicleChange() {
      this.isChangeVehicle = false
      this.$emit('assignVehicle', {
        routeOptimizationId: this.routeOptimizationId,
        currentVehicleId: this.currentVehicle.id,
        data: {
          action: 'ASSIGN',
          vehicle_id: this.selectedVehicleObject.id,
        },
      })
      this.$bvModal.hide('confirm-to-change-vehicle')
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../style.scss';

.v2-style {
  @import '../style-v2.scss';
}
</style>
